<template>
  <layout title="Classrooms">
    <div class="row">
      <div class="col-md-9">
        <h4>Sessions / {{ session.start_datetime }} / content</h4>
      </div>

      <div class="col-md-3">
        <inertia-link
          class="btn btn-light float-right"
          :href="route('classrooms.assigned.view.sessions', classroom.data.id)"
          preserve-scroll
        >
          Back
        </inertia-link>
      </div>
    </div>

    <hr />

    <div class="row">
      <div class="col-md-12">
        <FlashMessage :flash="flash" />
      </div>
    </div>

    <div class="row mb-2">
      <div class="col-md-12">
      <div class="card ">
        <div class="card-body">
          <div class="custom-control custom-switch">
          <input
            type="checkbox"
            class="custom-control-input"
            id="customSwitch1"
            :checked="isLive"
            @change="updateLiveStatus($event)"
          />
          <label class="custom-control-label" for="customSwitch1">
            Live Classroom
          </label>
        </div>
        </div>
      </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-8">
        <div class="form-check float-right">
          <input
            v-model="isMainContent"
            type="checkbox"
            class="form-check-input"
            id="main-content"
          />
          <label class="form-check-label" for="main-content">
            Mark as main lecture content
          </label>
        </div>
        <div class="form-group">
          <FileUploader @submitted="handleFileUploadSubmit" />
        </div>
      </div>

      <div class="col-md-4">
        <div>
          <button
            type="button"
            class="btn btn-sm btn-link float-right"
            @click.stop="showImportContentModal()"
          >
            Import from Course
          </button>
        </div>
      </div>
    </div>

    <hr />

    <h4>Uploaded content</h4>

    <div class="mt-4 row">
      <div
        v-for="content_item in content.files.data"
        :key="content_item.id"
        class="col-md-3 p-1"
      >
        <div class="card h-100">
          <span class="size-label">
            {{ formatBytes(content_item.meta_data.size, 0) }}
          </span>
          <span
            v-if="content_item.meta_data && content_item.meta_data.main_content"
            class="main-content-label"
            title="Main lecture content"
          >
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              class="bi bi-star-fill"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.283.95l-3.523 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"
              />
            </svg>
          </span>
          <img
            class="card-img-top"
            v-if="content_item.file_type == 'image/jpeg'"
            :src="
              getMediaItemLink(
                content_item.file_path,
                content_item.storage_disk
              )
            "
            alt="Card image cap"
          />
          <img
            class="card-img-top"
            v-else
            src="/images/files.png"
            alt="Card image cap"
          />

          <div class="file-box card-body" :title="content_item.name">
            <div class="row">
              <div
                style="overflow: hidden; text-overflow: ellipsis"
                class="col-md-11 p-0"
                :title="content_item.name"
              >
                {{ content_item.name }}
              </div>
              <div class="col-md-1 p-0">
                <button
                  class="btn btn-xs btn-danger float-right"
                  @click.stop="showDeleteConfirmationModal(content_item)"
                >
                  x
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-md-12">
        <nav aria-label="Page navigation example">
          <ul class="pagination justify-content-end">
            <li
              class="page-item"
              v-for="link in content.files.links"
              :class="{ active: link.active }"
              :key="link.label"
            >
              <inertia-link
                v-if="link.url"
                class="page-link"
                :href="link.url + '#content'"
                :only="['content']"
                preserve-scroll
              >
                <span v-html="link.label"></span>
              </inertia-link>
              <span class="page-link" v-else>
                <span v-html="link.label"></span>
              </span>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Modals - import -->
    <div
      class="modal fade"
      id="importContentModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-xl" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLabel">
              Import shared content from course
            </h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="m-1 row">
              <div
                v-if="!course_content.length"
                role="alert"
                class="mt-2 mb-0 alert alert-info col-md-12"
              >
                No content to import
              </div>
              <div
                v-for="content_item in course_content"
                :key="content_item.id"
                class="col-md-2 p-1"
                @click="markForImport(content_item.id)"
              >
                <div
                  class="card h-100"
                  :class="{
                    'selected-for-import': bulkImportIds.includes(
                      content_item.id
                    ),
                  }"
                >
                  <span class="size-label">
                    {{ formatBytes(content_item.meta_data.size, 0) }}
                  </span>
                  <img
                    class="card-img-top"
                    v-if="content_item.file_type == 'image/jpeg'"
                    :src="
                      getMediaItemLink(
                        content_item.file_path,
                        content_item.storage_disk
                      )
                    "
                    alt="Card image cap"
                  />
                  <img
                    class="card-img-top"
                    v-else
                    src="/images/files.png"
                    alt="Card image cap"
                  />

                  <div class="file-box card-body" :title="content_item.name">
                    {{ content_item.name }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-primary"
              data-dismiss="modal"
              @click="importCourseData()"
              :disabled="!course_content.length || !bulkImportIds.length"
            >
              Import
              <span class="badge badge-light">
                {{ bulkImportIds.length }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals - delete -->

    <div
      class="modal fade"
      id="deleteConfirmationModal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="exampleModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLabel">
              Delete Record - {{ selectedRecord && selectedRecord.name }}
            </h5>
            <button
              type="button"
              class="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            Are you sure you want to delete the record -
            <strong>{{ selectedRecord && selectedRecord.name }}</strong>
            ?
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              data-dismiss="modal"
            >
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-danger"
              data-dismiss="modal"
              @click="deleteRecord(selectedRecord ? selectedRecord.id : null)"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </layout>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import ClassroomLayout from "../../Layouts/ClassroomLayout";
import FileUploader from "../../../Components/FileUploader";

export default {
  layout: [AppLayout, ClassroomLayout],
  props: {
    classroom: Object,
    session: Object,
    flash: Object,
    content: {
      files: Object,
    },
    course_content: Object,
  },
  components: {
    FileUploader: FileUploader,
  },
  data() {
    return {
      isMainContent: true,
      bulkImportIds: [],
      selectedRecord: null,
      isLive: _.get(this.session, 'metadata.live')
    };
  },
  methods: {
    handleFileUploadSubmit(files) {
      let data = new FormData();
      data.append("session_id", this.session.id);
      data.append("main_content", this.isMainContent);

      for (let i = 0; i < files.length; i++) {
        data.append("content_files[]", files[i]);
      }

      this.$inertia.post(
        route("classrooms.sessions.content.add", this.session.id),
        data
      );
    },
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    showImportContentModal(record) {
      this.bulkImportIds = [];
      this.selectedRecord = record;
      $("#importContentModal").modal("show");
    },
    markForImport(id) {
      if (this.bulkImportIds.includes(id)) {
        let index = this.bulkImportIds.indexOf(id);
        this.bulkImportIds.splice(index, 1);
      } else {
        this.bulkImportIds.push(id);
      }
    },
    importCourseData() {
      this.$inertia
        .put(route("classrooms.sessions.content.import", this.session.id), {
          media_ids: this.bulkImportIds,
        })
        .then(() => {
          this.bulkImportIds = [];
        });
    },
    showDeleteConfirmationModal(record) {
      this.selectedRecord = record;
      $("#deleteConfirmationModal").modal("show");
    },
    deleteRecord(id) {
      this.$inertia.delete(
        route("classrooms.sessions.content.delete", [this.session.id, id])
      );
    },
  updateLiveStatus(e) {
    this.$inertia.put(
        route("classrooms.sessions.status.live", this.session.id),
        {
          live: e.target.checked
        }
      );
  }
  },
};
</script>